import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { PRIORITIZATION_VIEW_TOUR_DISMISSED } from './cardCoversSpotlight.constants.tsx';
import type { PrioritizationViewTourSteps, Props, State } from './GlobalFieldsTourSteps.types.tsx';
import { maybeWaitForPremiumModal } from './maybeWaitForPremiumModal.tsx';

export const loadPrioritizationViewTourDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().prioritizationView.tourDismissed === 'boolean') {
			return;
		}

		try {
			const tourDismissed = await getUserProperty<boolean>(
				accountId,
				PRIORITIZATION_VIEW_TOUR_DISMISSED,
			);

			const state = getState();

			setState({
				...state,
				prioritizationView: {
					...state.prioritizationView,
					tourDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					prioritizationView: {
						...state.prioritizationView,
						tourDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.prioritization-view.tour',
				`Failed to get user property with key: ${PRIORITIZATION_VIEW_TOUR_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const setPrioritizationViewTourDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			prioritizationView: {
				...state.prioritizationView,
				tourDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, PRIORITIZATION_VIEW_TOUR_DISMISSED, 'true');
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.prioritization-view.tour',
				`Failed to set user property with key: ${PRIORITIZATION_VIEW_TOUR_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const setPrioritizationViewTourSpotlightStep =
	(step: PrioritizationViewTourSteps | null): Action<State> =>
	({ getState, setState }) => {
		const state = getState();

		setState({
			...state,
			prioritizationView: {
				...state.prioritizationView,
				tourSpotlightStep: step,
			},
		});
	};
